import { gql } from "@apollo/client";
import {
    Button,
  Card,
  CardActions,
  CardContent,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import { isEqual } from "lodash";

import Help from "~/components/common/Help";
import { FhirServerFhirProviderEnum, FhirServersQuery, useFhirServersQuery, useUpdateFhirServerMutation } from "~/graphql/__generated__/graphql";
import { fhirProviders } from "@caredna-marlin/utils/organizationTypes";

type FhirServerProps = {
  fhirServer: FhirServersQuery["fhirServer"][number],
};

const isUpperEnv = import.meta.env.VITE_WEB_HOST.includes("medarrive");

type FhirProviderSelectProps = {
  disabled: boolean
  fhirProvider: FhirServerFhirProviderEnum
  onChange: (fp: FhirServerFhirProviderEnum) => void
}

export const FhirProviderSelect = ({ disabled, fhirProvider, onChange }: FhirProviderSelectProps) => <TextField
  select
  size="small"
  sx={{ width: "8em" }}
  value={fhirProvider}
  disabled={disabled}
  onChange={e => onChange(e.target.value as FhirServerFhirProviderEnum)}
>
  {fhirProviders
    .filter(p => !isUpperEnv || !p.startsWith("DEV"))
    .map(provider => <MenuItem value={provider} key={provider}>{provider}</MenuItem>)
  }
</TextField>

const FhirServer = ({ fhirServer }: FhirServerProps) => {
  const [local, setLocal] = useState(fhirServer);

  const { data } = useFhirServersQuery();
  const hl7FolderAlreadyInUse = !!data?.fhirServer.find(fs => fs.id !== fhirServer.id && local.hl7Folder && fs.hl7Folder === local.hl7Folder)

  const [updateFhirServer, { loading }] = useUpdateFhirServerMutation();

  const noLocalChanges = isEqual(local, fhirServer);

  const onSave = async () => {
    await updateFhirServer({
      variables: {
        ...local,
        fhirServerId: local.id,
        hl7Folder: local.hl7Folder ?? "",
      },
      awaitRefetchQueries: true,
      refetchQueries: ["FhirServers"],
    });
  };

  return <Card sx={{ height: "100%", pl: 1 }}>
    <CardContent>
      <Grid container sx={{ alignItems: "center" }} spacing={2}>
        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
          Active
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <Switch
            checked={local.active}
            disabled={loading}
            onChange={e => { setLocal({...local, active: e.target.checked })}}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
          FHIR Provider
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <FhirProviderSelect
            disabled={loading}
            fhirProvider={local.fhirProvider}
            onChange={fhirProvider => setLocal({ ...local, fhirProvider })}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
          URL
          <Help text={<>
            <p>Base URL of fhir resources. It might look something like:</p>
            <p><code>https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4/</code></p>
           </>} />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <TextField
            autoFocus
            size="small"
            type="text"
            placeholder="https://"
            sx={{ width: "25em" }}
            value={local.url}
            onChange={e => setLocal({...local, url: e.target.value })}
            disabled={!local.active || loading}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
          HL7 Path
          <Help text={<>
            <p>Use this FHIR server as the data source for HL7 messages uploaded on this path. Eg, if your system uploads:</p>
            <p><code>/MH1/2026-01-01-12345.hl7</code></p>
            <p>then put <code>MH1</code> in this field to match those messages with this server.</p>
            <p>Must be unique among your configured FHIR servers.</p>
          </>} />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <TextField
            autoFocus
            size="small"
            type="text"
            sx={{ width: "25em" }}
            value={local.hl7Folder}
            onChange={e => setLocal({...local, hl7Folder: e.target.value })}
            disabled={!local.active || loading}
            error={hl7FolderAlreadyInUse}
            helperText={hl7FolderAlreadyInUse ? "HL7 folder must be unique" : ""}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 4, lg: 3, xl: 3 }}>
          Use for SMART Login
          <Help text="If checked, this FHIR server is allowed to authenticate users. Please discuss with MedArrive for help setting this up." />
        </Grid>
        <Grid size={{ xs: 12, md: 8, lg: 9, xl: 9 }}>
          <Switch
            checked={local.useForLogin}
            disabled={!local.active || loading}
            onChange={e => { setLocal({...local, useForLogin: e.target.checked })}}
          />
        </Grid>
      </Grid>
    </CardContent>
    <CardActions>
      <Button variant="contained" size="small" color="primary" disabled={noLocalChanges || hl7FolderAlreadyInUse} onClick={onSave} loading={loading}>
        Save
      </Button>
      <Button variant="outlined" size="small" color="secondary" disabled={loading} onClick={() => setLocal(fhirServer)}>
        Reset
      </Button>
    </CardActions>
  </Card>
}

export default FhirServer;

gql(`
  mutation UpdateFhirServer(
    $fhirServerId: Int!
    $fhirProvider: FhirServerFhirProviderEnum!
    $useForLogin: Boolean!
    $url: String!
    $hl7Folder: String!
    $active: Boolean!
  )
  @permission(requires: manageOrgConfig)
  {
    updateFhirServer(
      id: $fhirServerId
      fhirProvider: $fhirProvider
      useForLogin: $useForLogin
      url: $url
      hl7Folder: $hl7Folder
      active: $active
    ) { id }
  }
`);
